import { useState, useEffect } from 'react';
import {
  Box,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Section, SectionCard, SectionHeader } from '@/components/common';
import { useUpdateActivityRatings } from '@/queries/useJobEnds';
import type { ActivityRating, ActivityRatingScale, ActivityType } from '@/api/types/jobEnd.types';
import {
  ACTIVITY_LABELS,
  ACTIVITY_RATING_LABELS,
  ACTIVITY_RATING_OPTIONS,
} from './natureOfRoleLabels';

// Below this the desktop split would leave a lopsided pair of stub tables, so one table reads better.
const MIN_ACTIVITIES_FOR_SPLIT = 6;

interface NatureOfRoleSectionProps {
  activities: ActivityType[];
  activityRatings: ActivityRating[];
  canEdit: boolean;
  jobEndId: number;
  error?: boolean;
  rowVersion?: string;
}

export function NatureOfRoleSection({
  activities,
  activityRatings,
  canEdit,
  jobEndId,
  error,
  rowVersion,
}: NatureOfRoleSectionProps) {
  const [localRatings, setLocalRatings] = useState<ActivityRating[]>(activityRatings);
  const { mutate: updateRatings } = useUpdateActivityRatings();

  useEffect(() => {
    setLocalRatings(activityRatings);
  }, [activityRatings]);

  const getRating = (activityType: ActivityType): ActivityRatingScale | null => {
    const rating = localRatings.find((r) => r.activityType === activityType);
    return rating?.rating ?? null;
  };

  const handleRatingChange = (activityType: ActivityType, value: ActivityRatingScale) => {
    const newRatings = localRatings.map((r) =>
      r.activityType === activityType ? { ...r, rating: value } : r
    );

    if (!localRatings.find((r) => r.activityType === activityType)) {
      newRatings.push({ activityType, rating: value });
    }

    setLocalRatings(newRatings);

    updateRatings(
      { id: jobEndId, ratings: newRatings, rowVersion },
      {
        onError: () => {
          setLocalRatings(activityRatings);
        },
      }
    );
  };

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isSplit = isDesktop && activities.length >= MIN_ACTIVITIES_FOR_SPLIT;
  const splitAt = Math.ceil(activities.length / 2);

  return (
    <Section>
      <SectionHeader error={error}>NATURE OF THE ROLE</SectionHeader>
      <SectionCard>
        {isSplit ? (
          <DesktopTable
            leftActivities={activities.slice(0, splitAt)}
            rightActivities={activities.slice(splitAt)}
            getRating={getRating}
            onRatingChange={handleRatingChange}
            disabled={!canEdit}
            error={error}
          />
        ) : (
          <MobileTable
            activities={activities}
            getRating={getRating}
            onRatingChange={handleRatingChange}
            disabled={!canEdit}
            error={error}
          />
        )}
      </SectionCard>
    </Section>
  );
}

interface TableProps {
  getRating: (activityType: ActivityType) => ActivityRatingScale | null;
  onRatingChange: (activityType: ActivityType, value: ActivityRatingScale) => void;
  disabled: boolean;
  error?: boolean;
}

interface DesktopTableProps extends TableProps {
  leftActivities: ActivityType[];
  rightActivities: ActivityType[];
}

interface MobileTableProps extends TableProps {
  activities: ActivityType[];
  startIndex?: number;
}

function DesktopTable({ leftActivities, rightActivities, getRating, onRatingChange, disabled, error }: DesktopTableProps) {
  return (
    <Box sx={{ display: 'flex', gap: 4.5, alignItems: 'flex-start' }}>
      <ActivityTable
        activities={leftActivities}
        getRating={getRating}
        onRatingChange={onRatingChange}
        disabled={disabled}
        error={error}
      />
      <ActivityTable
        activities={rightActivities}
        startIndex={leftActivities.length}
        getRating={getRating}
        onRatingChange={onRatingChange}
        disabled={disabled}
        error={error}
      />
    </Box>
  );
}

function ActivityTable({ activities, startIndex = 0, getRating, onRatingChange, disabled, error }: MobileTableProps) {
  return (
    <Table size="small" sx={{ flex: 1 }}>
      <TableHead>
        <TableRow>
          <TableCell width="40%">ACTIVITY</TableCell>
          {ACTIVITY_RATING_OPTIONS.map((option) => (
            // Trimmed padding keeps "Lightly Tested" on one line in the split desktop layout.
            <TableCell key={option} align="center" width="20%" sx={{ px: 1 }}>
              {ACTIVITY_RATING_LABELS[option]}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {activities.map((activityType, index) => {
          const isMissing = error && getRating(activityType) === null;
          return (
            <TableRow key={activityType} sx={isMissing ? { backgroundColor: '#fff0f0' } : undefined}>
              <TableCell sx={isMissing ? { color: 'error.main', fontWeight: 600 } : undefined}>
                {startIndex + index + 1}. {ACTIVITY_LABELS[activityType]}
              </TableCell>
              {ACTIVITY_RATING_OPTIONS.map((option) => (
                <TableCell key={option} align="center">
                  <Radio
                    value={option}
                    size="small"
                    disabled={disabled}
                    checked={getRating(activityType) === option}
                    onChange={() => onRatingChange(activityType, option)}
                    inputProps={{
                      'aria-label': `${ACTIVITY_LABELS[activityType]}: ${ACTIVITY_RATING_LABELS[option]}`,
                    }}
                    sx={isMissing ? { color: 'error.main' } : undefined}
                  />
                </TableCell>
              ))}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function MobileTable({ activities, getRating, onRatingChange, disabled, error }: MobileTableProps) {
  return (
    <ActivityTable
      activities={activities}
      getRating={getRating}
      onRatingChange={onRatingChange}
      disabled={disabled}
      error={error}
    />
  );
}

export default NatureOfRoleSection;
